import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CogIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../hooks/useAuth';
import { usePlanInfo, isTrialPlan } from '../hooks/usePlanInfo';
import { apiClient } from '../lib/apiClient';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import ChannelSelectionModal from '../components/ChannelSelectionModal';
import SlackImportModal from '../components/SlackImportModal';
import SalesforceAccountImportModal from '../components/SalesforceAccountImportModal';
import HubSpotCompanyImportModal from '../components/HubSpotCompanyImportModal';
import ZendeskImportModal from '../components/ZendeskImportModal';
import JiraImportModal from '../components/JiraImportModal';
import TrelloImportModal from '../components/TrelloImportModal';
import LinearImportModal from '../components/LinearImportModal';
import MondayImportModal from '../components/MondayImportModal';
import GoogleDriveImportModal from '../components/GoogleDriveImportModal';
import DataMappingModal from '../components/DataMappingModal';
import SlackIcon from '../components/icons/SlackIcon';
import GongIcon from '../components/icons/GongIcon';
import SalesforceIcon from '../components/icons/SalesforceIcon';
import HubSpotIcon from '../components/icons/HubSpotIcon';
import ZendeskIcon from '../components/icons/ZendeskIcon';
import JiraIcon from '../components/icons/JiraIcon';
import TrelloIcon from '../components/icons/TrelloIcon';
import LinearIcon from '../components/icons/LinearIcon';
import MondayIcon from '../components/icons/MondayIcon';
import GoogleDriveIcon from '../components/icons/GoogleDriveIcon';

interface Integration {
  id: string;
  name: string;
  type: string;
  description: string;
  icon: any;
  status: 'connected' | 'disconnected' | 'error' | 'coming-soon';
  color: string;
  oauth_url?: string;
  last_sync?: string;
  workspace_name?: string;
  importButtonText?: string;
}

interface IntegrationsData {
  slack?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    config?: {
      team_name?: string;
    };
  };
  gong?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    config?: {
      workspace_name?: string;
    };
  };
  salesforce?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      instance_url?: string;
    };
  };
  hubspot?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      portal_id?: string;
    };
  };
  zendesk?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      subdomain?: string;
    };
  };
  jira?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      site_name?: string;
      site_url?: string;
    };
  };
  trello?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      member_id?: string;
      username?: string;
      full_name?: string;
      email?: string;
      workspaces?: Array<{ id: string; name?: string; displayName?: string }>;
    };
  };
  linear?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      viewer_id?: string;
      viewer_name?: string;
      viewer_email?: string;
      organization_id?: string;
      organization_name?: string;
      organization_url_key?: string;
    };
  };
  monday?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      account_name?: string;
      user_name?: string;
      user_email?: string;
    };
  };
  'google-drive'?: {
    status: string;
    oauth_url?: string;
    last_sync?: string;
    last_sync_count?: number;
    config?: {
      user_id?: string;
    };
  };
}

interface OAuthResponse {
  oauth_url: string;
  state?: string;
  message?: string;
}

const IntegrationsPage: React.FC = () => {
  const { user } = useAuth();
  const { data: planInfo } = usePlanInfo();
  const queryClient = useQueryClient();
  const [urlParams] = useState(new URLSearchParams(window.location.search));
  const [showChannelModal, setShowChannelModal] = useState<{
    integrationType: string;
    channels: any[];
  } | null>(null);
  const [showSlackImportModal, setShowSlackImportModal] = useState(false);
  const [showSalesforceModal, setShowSalesforceModal] = useState(false);
  const [showHubSpotModal, setShowHubSpotModal] = useState(false);
  const [showZendeskModal, setShowZendeskModal] = useState(false);
  const [showJiraModal, setShowJiraModal] = useState(false);
  const [showTrelloModal, setShowTrelloModal] = useState(false);
  const [showLinearModal, setShowLinearModal] = useState(false);
  const [showMondayModal, setShowMondayModal] = useState(false);
  const [showGoogleDriveModal, setShowGoogleDriveModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState<'zendesk' | 'jira' | 'trello' | 'linear' | 'monday' | null>(null);

  // Check for OAuth callback results
  useEffect(() => {
    const slackResult = urlParams.get('slack');
    const salesforceResult = urlParams.get('salesforce');
    const zendeskResult = urlParams.get('zendesk');
    const jiraResult = urlParams.get('jira');
    const trelloResult = urlParams.get('trello');
    const linearResult = urlParams.get('linear');
    const mondayResult = urlParams.get('monday');
    const googleDriveResult = urlParams.get('googleDrive');
    const message = urlParams.get('message');
    
    if (slackResult === 'success') {
      alert('Slack integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      // Invalidate both integrations data AND channel management data
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      queryClient.invalidateQueries({ queryKey: ['slack-channel-management'] });
    } else if (slackResult === 'error') {
      alert(`Slack integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (salesforceResult === 'success') {
      alert('Salesforce integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (salesforceResult === 'error') {
      alert(`Salesforce integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (zendeskResult === 'success') {
      alert('Zendesk integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (zendeskResult === 'error') {
      alert(`Zendesk integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (jiraResult === 'success') {
      alert('Jira integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (jiraResult === 'error') {
      alert(`Jira integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (trelloResult === 'success') {
      alert('Trello integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (trelloResult === 'error') {
      alert(`Trello integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (linearResult === 'success') {
      alert('Linear integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (linearResult === 'error') {
      alert(`Linear integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (mondayResult === 'success') {
      alert('Monday integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (mondayResult === 'error') {
      alert(`Monday integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (googleDriveResult === 'success') {
      alert('Google Drive integration completed successfully!');
      window.history.replaceState({}, document.title, window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    } else if (googleDriveResult === 'error') {
      alert(`Google Drive integration failed: ${message || 'Unknown error'}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [urlParams, queryClient]);

  // Fetch integrations data
  const { data: integrationsData, isLoading, error } = useQuery<IntegrationsData>({
    queryKey: ['integrations'],
    // Pass the apiClient method directly. It's already an async function
    // that returns the correct data shape.
    queryFn: apiClient.getIntegrations,
    enabled: !!user,
  });

  // Connect integration mutation
  const connectMutation = useMutation<OAuthResponse, Error, string>({
    mutationFn: async (integrationType: string) => {
      if (integrationType === 'zendesk') {
        return (apiClient as any).authorizeZendesk();
      } else if (integrationType === 'jira') {
        const response = await apiClient.get('/api/integrations/jira/authorize');
        return response.data;
      } else if (integrationType === 'trello') {
        const response = await (apiClient as any).authorizeTrello();
        return response.data ?? response;
      } else if (integrationType === 'linear') {
        const response = await (apiClient as any).authorizeLinear();
        return response.data ?? response;
      } else if (integrationType === 'monday') {
        const response = await apiClient.get('/api/integrations/monday/authorize');
        return response.data ?? response;
      } else if (integrationType === 'google-drive') {
        const response = await apiClient.get('/api/integrations/google-drive/oauth');
        return response.data ?? response;
      }
      return apiClient.customRequest<OAuthResponse>(`/api/integrations/${integrationType}/oauth`);
    },
    onSuccess: (data) => {
      const url = (data as any).data?.oauth_url || (data as any).oauth_url || (data as any).authorization_url;
      if (url) {
        window.location.href = url;
      } else {
        alert('Failed to get authorization URL');
      }
    },
    onError: (error) => {
      alert(`Failed to connect: ${error.message}`);
    }
  });

  // Disconnect integration mutation
  const disconnectMutation = useMutation<any, Error, string>({
    mutationFn: async (integrationType: string) => {
      if (integrationType === 'zendesk') {
        return (apiClient as any).disconnectZendesk();
      } else if (integrationType === 'jira') {
        return apiClient.delete('/api/integrations/jira/disconnect');
      } else if (integrationType === 'trello') {
        return (apiClient as any).disconnectTrello();
      } else if (integrationType === 'linear') {
        return (apiClient as any).disconnectLinear();
      } else if (integrationType === 'monday') {
        return apiClient.delete('/api/integrations/monday/disconnect');
      } else if (integrationType === 'google-drive') {
        return apiClient.delete('/api/integrations/google-drive/disconnect');
      }
      return apiClient.deleteIntegration(integrationType);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      alert('Integration disconnected successfully!');
    },
    onError: (error) => {
      alert(`Failed to disconnect: ${error.message}`);
    }
  });

  // Fetch channels for import selection
  const fetchChannelsMutation = useMutation<any[], Error, string>({
    mutationFn: async (integrationType: string) => {
      if (integrationType === 'slack') {
        return apiClient.customRequest(`/api/integrations/slack/channels`);
      }
      return [];
    },
    onSuccess: (channels, integrationType) => {
      setShowChannelModal({ integrationType, channels });
    },
    onError: (error) => {
      alert(`Failed to fetch channels: ${error.message}`);
    }
  });

  // Import selected channels data mutation
  const importDataMutation = useMutation<any, Error, { integrationType: string; channelIds: string[] }>({
    mutationFn: async ({ integrationType, channelIds }) => {
      return apiClient.customRequest(`/integrations/${integrationType}/import-channels`, { 
        method: 'POST',
        body: { channel_ids: channelIds }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      setShowChannelModal(null);
      alert('Data import started successfully! This may take a few minutes to complete.');
    },
    onError: (error) => {
      alert(`Failed to import data: ${error.message}`);
    }
  });

  // Salesforce sync mutation
  const salesforceSyncMutation = useMutation<any, Error>({
    mutationFn: async () => {
      return apiClient.customRequest('/api/integrations/salesforce/sync', { 
        method: 'POST'
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      const message = data.accounts_synced 
        ? `Successfully synced ${data.accounts_synced} accounts from Salesforce!`
        : 'Salesforce sync completed!';
      alert(message);
    },
    onError: (error) => {
      alert(`Failed to sync Salesforce data: ${error.message}`);
    }
  });

  // Zendesk sync mutation
  const zendeskSyncMutation = useMutation<any, Error>({
    mutationFn: async () => {
      return (apiClient as any).syncZendeskData();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
      const message = data.data 
        ? `Successfully synced ${data.data.tickets} tickets, ${data.data.users} users, ${data.data.organizations} organizations, and ${data.data.satisfaction_ratings} satisfaction ratings from Zendesk!`
        : 'Zendesk sync completed!';
      alert(message);
    },
    onError: (error) => {
      alert(`Failed to sync Zendesk data: ${error.message}`);
    }
  });


  const integrations: Integration[] = useMemo(() => {
    // The API data, which is now correctly shaped
    const data = integrationsData || {};

    return [
      {
        id: 'slack',
        name: 'Slack',
        type: 'slack',
        description: 'Connect your Slack workspace to analyze team communications and customer conversations',
        icon: SlackIcon,
        status: data.slack?.status === 'connected' ? 'connected' : 'disconnected',
        color: '#4A154B',
        oauth_url: data.slack?.oauth_url,
        last_sync: data.slack?.last_sync,
        workspace_name: data.slack?.config?.team_name,
        importButtonText: 'Import All Slack Data'
      },
      {
        id: 'gong',
        name: 'Gong',
        type: 'gong',
        description: 'Sync call recordings and transcripts for comprehensive customer insights and analysis',
        icon: GongIcon,
        status: data.gong?.status === 'connected' ? 'connected' : 'disconnected',
        color: '#FF6B35',
        oauth_url: data.gong?.oauth_url,
        last_sync: data.gong?.last_sync,
        workspace_name: data.gong?.config?.workspace_name,
        importButtonText: 'Import All Gong Data'
      },
      {
        id: 'salesforce',
        name: 'Salesforce',
        type: 'salesforce',
        description: 'Sync customer accounts from Salesforce CRM for comprehensive customer insights',
        icon: SalesforceIcon,
        status: data.salesforce?.status === 'connected' ? 'connected' : 'disconnected',
        color: '#0176D3',
        oauth_url: data.salesforce?.oauth_url,
        last_sync: data.salesforce?.last_sync,
        workspace_name: data.salesforce?.config?.instance_url,
        importButtonText: 'Select Accounts to Import'
      },
      {
        id: 'hubspot',
        name: 'HubSpot',
        type: 'hubspot',
        description: 'Sync company records from HubSpot CRM for comprehensive customer insights',
        icon: HubSpotIcon,
        status: data.hubspot?.status === 'connected' ? 'connected' : 'disconnected',
        color: '#FF7A59',
        oauth_url: data.hubspot?.oauth_url,
        last_sync: data.hubspot?.last_sync,
        workspace_name: data.hubspot?.config?.portal_id,
        importButtonText: 'Select Companies to Import'
      },
      {
        id: 'zendesk',
        name: 'Zendesk',
        type: 'zendesk',
        description: 'Sync support tickets and customer satisfaction data from Zendesk for comprehensive customer insights',
        icon: ZendeskIcon,
        status: (data.zendesk?.status === 'connected' || data.zendesk?.status === 'active') ? 'connected' : 'disconnected',
        color: '#17494D',
        oauth_url: data.zendesk?.oauth_url,
        last_sync: data.zendesk?.last_sync,
        workspace_name: data.zendesk?.config?.subdomain,
        importButtonText: 'Select Data to Import'
      },
      {
        id: 'jira',
        name: 'Jira',
        type: 'jira',
        description: 'Sync issues and projects from Jira to correlate customer feedback with engineering work',
        icon: JiraIcon,
        status: (data.jira?.status === 'connected' || data.jira?.status === 'active') ? 'connected' : 'disconnected',
        color: '#2684FF',
        oauth_url: data.jira?.oauth_url,
        last_sync: data.jira?.last_sync,
        workspace_name: data.jira?.config?.site_name,
        importButtonText: 'Select Projects to Import'
      },
      {
        id: 'trello',
        name: 'Trello',
        type: 'trello',
        description: 'Sync boards, lists and cards from Trello to correlate customer feedback with project work',
        icon: TrelloIcon,
        status: (data.trello?.status === 'connected' || data.trello?.status === 'active') ? 'connected' : 'disconnected',
        color: '#0079BF',
        oauth_url: data.trello?.oauth_url,
        last_sync: data.trello?.last_sync,
        workspace_name: data.trello?.config?.full_name || data.trello?.config?.username,
        importButtonText: 'Select Boards to Import'
      },
      {
        id: 'linear',
        name: 'Linear',
        type: 'linear',
        description: 'Sync issues and teams from Linear to surface engineering work alongside customer feedback',
        icon: LinearIcon,
        status: (data.linear?.status === 'connected' || data.linear?.status === 'active') ? 'connected' : 'disconnected',
        color: '#5E6AD2',
        oauth_url: data.linear?.oauth_url,
        last_sync: data.linear?.last_sync,
        workspace_name: data.linear?.config?.organization_name || data.linear?.config?.viewer_name,
        importButtonText: 'Select Teams to Import'
      },
      {
        id: 'monday',
        name: 'Monday.com',
        type: 'monday',
        description: 'Sync boards and items from Monday.com to connect customer work and updates with insights',
        icon: MondayIcon,
        status: (data.monday?.status === 'connected' || data.monday?.status === 'active') ? 'connected' : 'disconnected',
        color: '#111827',
        oauth_url: data.monday?.oauth_url,
        last_sync: data.monday?.last_sync,
        workspace_name: data.monday?.config?.account_name || data.monday?.config?.user_name,
        importButtonText: 'Select Boards to Import'
      },
      {
        id: 'google-drive',
        name: 'Google Drive',
        type: 'google-drive',
        description: 'Ingest documents from Google Drive (read-only) with customer-aware folder/file mapping',
        icon: GoogleDriveIcon,
        status: (data['google-drive']?.status === 'connected' || data['google-drive']?.status === 'active') ? 'connected' : 'disconnected',
        color: '#4285F4',
        oauth_url: data['google-drive']?.oauth_url,
        last_sync: data['google-drive']?.last_sync,
        workspace_name: 'Drive',
        importButtonText: 'Browse & Import Documents'
      }
    ];
  }, [integrationsData]);

  // Integration statuses are now properly handled without debug logging

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/20"><CheckCircleIcon className="h-3 w-3 mr-1" />Connected</Badge>;
      case 'error':
        return <Badge variant="destructive"><ExclamationCircleIcon className="h-3 w-3 mr-1" />Error</Badge>;
      case 'coming-soon':
        return <Badge variant="secondary"><CogIcon className="h-3 w-3 mr-1" />Coming Soon</Badge>;
      default:
        return <Badge variant="outline"><CogIcon className="h-3 w-3 mr-1" />Not Connected</Badge>;
    }
  };

  const handleConnect = (integration: Integration) => {
    if (integration.status === 'coming-soon') return;
    
    // Disable non-Slack integrations for trial accounts
    if (isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack') {
      alert('This integration is only available for paid plans. Please upgrade to access Gong and other integrations.');
      return;
    }
    
    if (integration.status === 'connected') {
      if (window.confirm(`Are you sure you want to disconnect ${integration.name}?`)) {
        disconnectMutation.mutate(integration.type);
      }
    } else {
      // Handle Zendesk OAuth flow
      if (integration.type === 'zendesk') {
        connectMutation.mutate(integration.type);
      } else {
        connectMutation.mutate(integration.type);
      }
    }
  };

  const handleImportData = (integration: Integration) => {
    // Disable non-Slack integrations for trial accounts
    if (isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack') {
      alert('This integration is only available for paid plans. Please upgrade to access Gong and other integrations.');
      return;
    }
    
    // Use new sophisticated SlackImportModal for Slack
    if (integration.type === 'slack') {
      setShowSlackImportModal(true);
    } else if (integration.type === 'salesforce') {
      // Use new SalesforceAccountImportModal for selective import
      setShowSalesforceModal(true);
    } else if (integration.type === 'hubspot') {
      // Use new HubSpotCompanyImportModal for selective import
      setShowHubSpotModal(true);
    } else if (integration.type === 'zendesk') {
      // Use new ZendeskImportModal for selective import
      setShowZendeskModal(true);
    } else if (integration.type === 'jira') {
      // Use new JiraImportModal for selective import
      setShowJiraModal(true);
    } else if (integration.type === 'trello') {
      setShowTrelloModal(true);
    } else if (integration.type === 'linear') {
      setShowLinearModal(true);
    } else if (integration.type === 'monday') {
      setShowMondayModal(true);
    } else if (integration.type === 'google-drive') {
      setShowGoogleDriveModal(true);
    } else {
      // Keep old flow for other integrations
      fetchChannelsMutation.mutate(integration.type);
    }
  };

  const handleChannelImport = async (channelIds: string[]) => {
    if (showChannelModal) {
      await importDataMutation.mutateAsync({
        integrationType: showChannelModal.integrationType,
        channelIds
      });
    }
  };

  const renderIntegrationIcon = (integration: Integration) => {
    const IconComponent = integration.icon;
    return <IconComponent className="h-10 w-10 flex-shrink-0" />;
  };

  const formatLastSync = (lastSync: string | undefined) => {
    if (!lastSync) return 'Never';
    return new Date(lastSync).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground">Loading integrations...</p>
        </div>
        <div className="flex justify-center p-8">
          <ArrowPathIcon className="h-8 w-8 text-muted-foreground animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground">Error loading integrations</p>
        </div>
        <div className="flex justify-center p-8">
          <div className="text-center">
            <ExclamationCircleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">Failed to load integrations</p>
            <p className="text-sm text-gray-500 mt-2">Please refresh the page or try again later</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Integrations</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Connect your tools to start analyzing customer conversations and extracting actionable insights
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {integrations.map((integration) => (
          <Card key={integration.id} className="flex flex-col">
            <CardHeader>
              <div className="flex items-center space-x-3 sm:space-x-4">
                {renderIntegrationIcon(integration)}
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base sm:text-lg truncate">{integration.name}</CardTitle>
                  <div className="mt-2">
                    {getStatusBadge(integration.status)}
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1">
              <CardDescription className="text-sm leading-relaxed">
                {integration.description}
              </CardDescription>

              {integration.status === 'connected' && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  {integration.workspace_name && (
                    <div className="text-xs text-green-800 dark:text-green-200 mb-1">
                      <strong>Workspace:</strong> <span className="truncate">{integration.workspace_name}</span>
                    </div>
                  )}
                  <div className="text-xs text-green-800 dark:text-green-200">
                    <strong>Last Sync:</strong> {formatLastSync(integration.last_sync)}
                  </div>
                </div>
              )}
            </CardContent>
            
            <CardFooter className="pt-4">
              {integration.status === 'connected' && (integration.type === 'slack' || integration.type === 'gong' || integration.type === 'salesforce' || integration.type === 'hubspot' || integration.type === 'zendesk' || integration.type === 'jira' || integration.type === 'trello' || integration.type === 'linear' || integration.type === 'monday' || integration.type === 'google-drive') ? (
                <div className="w-full space-y-3">
                  <Button 
                    className={`w-full font-medium py-2.5 px-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md ${
                      isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack'
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'
                    }`}
                    disabled={importDataMutation.isPending || salesforceSyncMutation.isPending || zendeskSyncMutation.isPending || (isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack')}
                    onClick={() => handleImportData(integration)}
                  >
                    {(importDataMutation.isPending || (integration.type === 'salesforce' && salesforceSyncMutation.isPending) || (integration.type === 'zendesk' && zendeskSyncMutation.isPending)) ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        <span>{integration.type === 'salesforce' || integration.type === 'zendesk' ? 'Syncing...' : 'Importing...'}</span>
                      </>
                    ) : isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack' ? (
                      <>
                        <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                        <span>Upgrade Required</span>
                      </>
                    ) : (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        <span>{integration.importButtonText || 'Import All Data'}</span>
                      </>
                    )}
                  </Button>
                  
                  {/* Map Organizations button for Zendesk, Jira, Trello, Linear, and Monday */}
                  {(integration.type === 'zendesk' || integration.type === 'jira' || integration.type === 'trello' || integration.type === 'linear' || integration.type === 'monday') && (
                    <Button 
                      variant="outline"
                      className="w-full font-medium py-2.5 px-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md border-2 border-purple-600 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                      onClick={() => setShowMappingModal(integration.type as 'zendesk' | 'jira' | 'trello' | 'linear' | 'monday')}
                    >
                      <CogIcon className="h-4 w-4 mr-2" />
                      <span>Map Organizations</span>
                    </Button>
                  )}
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-medium py-2.5 px-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md"
                    disabled={connectMutation.isPending || disconnectMutation.isPending || importDataMutation.isPending || salesforceSyncMutation.isPending || zendeskSyncMutation.isPending}
                    onClick={() => handleConnect(integration)}
                  >
                    {disconnectMutation.isPending ? (
                      <>
                        <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                        <span>Disconnecting...</span>
                      </>
                    ) : (
                      <>
                        <ExclamationCircleIcon className="h-4 w-4 mr-2" />
                        <span>Disconnect</span>
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <Button 
                  className={`w-full font-medium py-2.5 px-4 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md ${
                    integration.status === 'connected' 
                      ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white'
                      : integration.status === 'coming-soon'
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack'
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
                  }`}
                  disabled={integration.status === 'coming-soon' || (isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack') || connectMutation.isPending || disconnectMutation.isPending || importDataMutation.isPending || salesforceSyncMutation.isPending}
                  onClick={() => handleConnect(integration)}
                >
                  {connectMutation.isPending || disconnectMutation.isPending ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                      <span>{integration.status === 'connected' ? 'Disconnecting...' : 'Connecting...'}</span>
                    </>
                  ) : (
                    <>
                      {integration.status === 'connected' ? (
                        <>
                          <ExclamationCircleIcon className="h-4 w-4 mr-2" />
                          <span>Disconnect</span>
                        </>
                      ) : integration.status === 'coming-soon' ? (
                        <>
                          <CogIcon className="h-4 w-4 mr-2" />
                          <span>Coming Soon</span>
                        </>
                      ) : isTrialPlan(planInfo?.plan_id) && integration.type !== 'slack' ? (
                        <>
                          <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                          <span>Upgrade Required</span>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          <span>Connect</span>
                        </>
                      )}
                    </>
                  )}
                </Button>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* Channel Selection Modal (legacy for non-Slack integrations) */}
      {showChannelModal && (
        <ChannelSelectionModal
          channels={showChannelModal.channels}
          onClose={() => setShowChannelModal(null)}
          onImport={handleChannelImport}
          isLoading={fetchChannelsMutation.isPending}
        />
      )}

      {/* Slack Import Modal (new sophisticated flow) */}
      {showSlackImportModal && (
        <SlackImportModal
          onClose={() => setShowSlackImportModal(false)}
        />
      )}

      {/* Salesforce Account Import Modal */}
      {showSalesforceModal && (
        <SalesforceAccountImportModal
          onClose={() => setShowSalesforceModal(false)}
        />
      )}

      {/* HubSpot Company Import Modal */}
      {showHubSpotModal && (
        <HubSpotCompanyImportModal
          onClose={() => setShowHubSpotModal(false)}
        />
      )}

      {/* Zendesk Import Modal */}
      {showZendeskModal && (
        <ZendeskImportModal
          onClose={() => setShowZendeskModal(false)}
        />
      )}

      {/* Jira Import Modal */}
      {showJiraModal && (
        <JiraImportModal
          onClose={() => setShowJiraModal(false)}
        />
      )}

      {/* Trello Import Modal */}
      {showTrelloModal && (
        <TrelloImportModal
          onClose={() => setShowTrelloModal(false)}
        />
      )}

      {/* Linear Import Modal */}
      {showLinearModal && (
        <LinearImportModal
          onClose={() => setShowLinearModal(false)}
        />
      )}

      {/* Monday Import Modal */}
      {showMondayModal && (
        <MondayImportModal
          onClose={() => setShowMondayModal(false)}
        />
      )}

      {/* Google Drive Import Modal */}
      {showGoogleDriveModal && (
        <GoogleDriveImportModal
          onClose={() => setShowGoogleDriveModal(false)}
        />
      )}

      {/* Data Mapping Modal for Zendesk/Jira/Trello/Linear/Monday Organizations */}
      {showMappingModal && (
        <DataMappingModal
          integrationType={showMappingModal}
          onClose={() => setShowMappingModal(null)}
        />
      )}
    </div>
  );
};

export default IntegrationsPage;